/**
 * Controller for lightboxes/modals using a wrapped a11y-dialog.
 *
 * There are two parts to this; the typical ElementController that is annotated
 * at each place where a lightbox is desired, and the skeleton class that
 * handles the dialog element itself. The latter is due to how a11y-dialog
 * (and modals in general) function, where the HTML for the modal should exist
 * in the DOM before it's used and thus should be a single instance (per type
 * at least) to avoid bloating the page. This is also true for many other
 * ARIA-driven things.
 *
 * The skeleton class is hidden in an IIFE to prevent multiple instantiations,
 * enforcing the single element pattern. This class handles everything inside
 * the modal (close and navigation buttons, keyboard events, element sizing...)
 * with the help of an a11y-dialog instance while being ignorant about how its
 * items got there.
 *
 * The ElementController gets an instance for each group of items, where an
 * item is something that points to some kind of lightbox content. This can be
 * through a data attribute for an HTML modal or through image links for a
 * typical image gallery. Each item can be a trigger for itself, or have
 * multiple triggers. In short, an item is one 'slide' in the lightbox and a
 * trigger points to an item.
 *
 * The simplest version is image links with nothing explicit specified:
 *
 *     <div data-controller="Lightbox">
 *       <a href="large-dog.jpg">
 *         <img src="small-dog.jpg" alt="Dog">
 *       </a>
 *       <a href="large-cat.jpg" data-caption="Text about the cat">
 *         <img src="small-cat.jpg" alt="Cat">
 *       </a>
 *     </div>
 *
 * Each link to an image will become an item, with navigation between them
 * unless disabled. Use `data-caption` to display caption text below the image.
 * Multiple links to the same image will be deduped, i.e. all links will open
 * the modal but only the first will be used for the item content.
 *
 * To display HTML, use `data-lightbox-content` (here with previous/next
 * navigation disabled through `data-lightbox-has-nav`):
 *
 *     <div data-controller="Lightbox" data-lightbox-has-nav="false">
 *       <div>
 *         Some stuff
 *         <button
 *           type="button"
 *           data-lightbox-content="<p>More about the stuff</p>">
 *             Show more stuff
 *         </button>
 *       </div>
 *       <div>
 *         Some things
 *         <button
 *           type="button"
 *           data-lightbox-content="<p>These are our things</p>">
 *             Show more things
 *         </button>
 *       </div>
 *     </div>
 *
 * Multiple triggers for the same content can be achieved by moving the content
 * to some container and setting `data-lightbox-trigger` on children to that
 * container:
 *
 *     <div data-controller="Lightbox">
 *       <div data-lightbox-content="<p>More about the stuff</p>">
 *         <button type="button" data-lightbox-trigger>Show more stuff</button>
 *         Some long text about stuff
 *         <button type="button" data-lightbox-trigger>Show more stuff</button>
 *       </div>
 *       <div data-lightbox-content="<p>These are our things</p>">
 *         <button type="button" data-lightbox-trigger>Show more things</button>
 *         Some long text about things
 *         <button type="button" data-lightbox-trigger>Show more things</button>
 *       </div>
 *     </div>
 *
 * The simplest possible option is to put everything on the ElementController:
 *
 *     <button
 *       type="button"
 *       data-controller="Lightbox"
 *       data-lightbox-content="<p>More about the stuff</p>">
 *         Show more stuff
 *     </button>
 *
 *     <a data-controller="Lightbox" href="/cat.jpg">View cat</a>
 *
 * To fetch HTML via ajax, use `data-lightbox-url`:
 *
 *     <button
 *       type="button"
 *       data-controller="Lightbox"
 *       data-lightbox-url="https://example.com/more-about-the-stuff/">
 *         Show more stuff
 *     </button>
 *
 * You can use a lightbox controller within a lightbox. This will replace the
 * content of the currently opened lightbox.
 * A nav stack is created if `data-lightbox-url` is used in nested lightbox
 * nodes, and a back button appears to navigate backwards in the stack.
 *
 * The lightbox handles successful form responses by navigating backwards
 * if there is a nav stack with more than one url. Otherwise it will close the
 * lightbox.
 *
 * There are some options available via data attribute on the lightbox root
 * element for each instance:
 *
 * - `lightbox-has-nav`: If the previous/next navigation should be displayed
 *   when there are more than one item. Defaults to true.
 * - `lightbox-scrollable`: If modal content should be scrollable. Defaults to
 *   true for HTML and false for images.
 * - `lightbox-class`: Class name to set on the lightbox when opened.
 * - `lightbox-save-content-on-close`: If modal content should be saved
 *   back to the item's data-lightbox-content when lightbox closes.
 * - `lightbox-load-error-text`: Custom text content for Ajax loading errors.
 * - `id` (not data attribute): Lightbox ID, passed to events.
 *
 * The same options are available when opening programatically via the global
 * open event, but in camelCase and without the `lightbox-` prefix (i.e. send
 * `loadErrorText` for `lightbox-load-error-text`).
 *
 * An example that isn't related to this implementation as such, but covers
 * the requirements for proper and accessible use:
 * https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/dialog.html
 *
 * Events triggered on root node (data-controller="Lightbox"), or the global
 * event target node for programatically opened lightboxes:
 *
 * | Name                     | Arguments        | Cancelable | Triggered                   |
 * |--------------------------|------------------|------------|-----------------------------|
 * | LIGHTBOX_EVENT.ajaxError | error, id        |     ✓      | When there is an Ajax error |
 * | LIGHTBOX_EVENT.close     | content node, id |            | When lightbox closes        |
 * | LIGHTBOX_EVENT.open      | content node, id |            | After lightbox has opened   |
 * | LIGHTBOX_EVENT.showItem  | content node, id |            | After an item is displayed  |
 *
 * Global events, use with `triggerGlobalCustomEvent` to control the lightbox:
 *
 * | Name                 | Arguments            | Action             |
 * |----------------------|----------------------|--------------------|
 * | LIGHTBOX_EVENT.back  |                      | Navigate backwards |
 * | LIGHTBOX_EVENT.close |                      | Close lightbox     |
 * | LIGHTBOX_EVENT.open  | HTML or url, options | Open new lightbox  |
 *
 * @jsxRuntime classic
 * @jsx el
 * @jsxFrag el.fragment
 */

// LightboxSkeleton is internal
/* eslint-disable max-classes-per-file */

import A11yDialog from 'a11y-dialog';

import ElementController from '../../base-controllers/ElementController';
import EventManager from '../../EventManager';
import initializer from '../../initializer';
import modifierKeyWatcher from '../../modifierKeyWatcher';
import { conditionalAssign } from '../../utils/collection';
import {
  el,
  GLOBAL_EVENT_TARGET,
  getData,
  setData,
  selectAll,
  selectSingle,
  parseHTML,
  setInnerHTML,
  getInlineSVGAttrs,
} from '../../utils/dom';
import {
  afterNextPaint,
  debounce,
  isBool,
  isNil,
  isNumber,
  isTruthy,
  nextFrame,
  not,
  sleep,
} from '../../utils/js';
import { httpGet } from '../../utils/http';
import {
  FORM_EVENT,
  LIGHTBOX_CONTROLLER_ID,
  LIGHTBOX_EVENT,
} from '../../../constants';
import { i18n } from '../../i18n';
import sv from './Lightbox-sv';

i18n.addLocale('sv', sv);

// Items, see docblock
const LIGHTBOX_CONTENT_DATA_ATTRIBUTE = 'lightbox-content';
const LIGHTBOX_URL_DATA_ATTRIBUTE = 'lightbox-url';
const LIGHTBOX_TRIGGER_DATA_ATTRIBUTE = 'lightbox-trigger';
const LIGHTBOX_CAPTION_DATA_ATTRIBUTE = 'caption';

// Options
const OPTION_CLASS_DATA_ATTRIBUTE = 'lightbox-class';
const OPTION_HAS_NAV_DATA_ATTRIBUTE = 'lightbox-has-nav';
const OPTION_SAVE_CONTENT_ON_CLOSE_DATA_ATTRIBUTE =
  'lightbox-save-content-on-close';
const OPTION_SCROLLABLE_DATA_ATTRIBUTE = 'lightbox-scrollable';

// ID set on the first heading in the modal, used for aria-labelledby
const LIGHTBOX_TITLE_ID = 'lightbox-title';

// Class name that is toggled on <html> when the modal is open
const LIGHTBOX_OPEN_CLASS_NAME = 'lightbox-open';

// Class names set on the lightbox when opened, depending on if the content
// should be contained within the viewport or not.
const LIGHTBOX_CONTAINED_CLASS_NAME = 'lightbox--contained';
const LIGHTBOX_SCROLLABLE_CLASS_NAME = 'lightbox--scrollable';

// CLass names for default image elements
const LIGHTBOX_FIGURE_CLASS_NAME = 'lightbox-figure';
const LIGHTBOX_CAPTION_CLASS_NAME = 'lightbox-caption';

// Labels/texts and label templates, some visible and some for screen readers
const LOAD_ERROR_TEXT_DATA_ATTRIBUTE = 'lightbox-load-error-text';

// Internal
const SKELETON_ID = 'lightbox-skeleton';
const IMAGE_HREF_REGEX = /\.(jpe?g|a?png|gif|webp|svg|bmp)$/;
const TYPE_IMAGE = 'image';
const TYPE_HTML = 'html';
const URL_REGEX = /^https?:/;
const DEFAULT_OPTIONS = {
  class: '',
  hasNav: true,
  id: '',
  isScrollable: undefined,
  loadErrorText: '',
  saveContentOnClose: false,
};

/**
 * Get the single instance of LightboxSkeleton, the class for managing the
 * a11y-dialog instance.
 */
const getLightboxSkeleton = (() => {
  class LightboxSkeleton extends EventManager {
    constructor() {
      super();
      this.init();
    }

    /**
     * Run initial modal setup and bind events.
     */
    init() {
      this._setInitialState();
      this._ensureSkeleton();

      const root = document.getElementById(SKELETON_ID);

      // Save elements
      this.root = root;
      this.body = selectSingle(root, '[data-lightbox-body]');
      this.content = selectSingle(root, '[data-lightbox-content]');
      this.closeButton = selectSingle(root, '[data-lightbox-close]');
      this.backNav = selectSingle(root, 'back-nav');
      this.nav = selectSingle(root, '[data-lightbox-nav]');
      this.prevButton = selectSingle(root, '[data-lightbox-prev]');
      this.nextButton = selectSingle(root, '[data-lightbox-next]');
      this.prevPreload = selectSingle(root, '[data-lightbox-prev-preload]');
      this.nextPreload = selectSingle(root, '[data-lightbox-next-preload]');
      this.liveRegion = selectSingle(root, '[data-lightbox-live]');

      // Init a11y-dialog. Create custom hide method to be able to stop it from
      // closing if controllers can't be destroyed.
      const originalHide = A11yDialog.prototype.hide;
      this._a11yModalOriginalHide = originalHide;
      A11yDialog.prototype.hide = function LightboxA11yDialogHide(event) {
        // Returns a boolean indicating if destroying was successful.
        if (!initializer.destroy(root)) {
          if (event && event.preventDefault) {
            event.preventDefault();
          }
          return this;
        }
        return originalHide.call(this, event);
      };
      this.a11yModal = new A11yDialog(root);

      this._bindEvents();
    }

    /**
     * Unbind events and remove all references.
     */
    destroy() {
      /* eslint-disable no-multi-assign */
      this._unbindEvents();
      A11yDialog.prototype.hide = this._a11yModalOriginalHide;
      this.a11yModal.destroy();
      this.a11yModal = null;
      this.root.remove();
      this.root =
        this.body =
        this.content =
        this.closeButton =
        this.backNav =
        this.nav =
        this.prevButton =
        this.nextButton =
        this.prevPreload =
        this.nextPreload =
        this.liveRegion =
          null;
      this._setInitialState();
    }

    /**
     * Set initial properties for the closed state.
     */
    _setInitialState() {
      this.controllerRoot = null;
      this.items = [];
      this.currentIndex = 0;
      this.isOpen = false;
      this.type = null;
      this.hasNav = false;
      this.isScrollable = true;
      this.customClassNames = [];
      this.isSettingHTML = false;
      this.navStack = [];
    }

    /**
     * Insert the skeleton DOM nodes if they aren't already on the page.
     */
    _ensureSkeleton() {
      if (document.getElementById(SKELETON_ID)) {
        return;
      }
      const SvgIcon = ({ width, height, children }) => (
        <svg {...getInlineSVGAttrs(width, height)}>{children}</svg>
      );
      const skeleton = (
        <div
          id={SKELETON_ID}
          aria-labelledby={LIGHTBOX_TITLE_ID}
          class="lightbox"
          aria-hidden="true"
        >
          <div class="lightbox-overlay" data-a11y-dialog-hide></div>
          <div class="lightbox-body" role="document" data-lightbox-body>
            <div
              class="visuallyhidden"
              aria-live="polite"
              aria-atomic="true"
              data-lightbox-live
            ></div>
            <div class="lightbox-content-wrap">
              <button
                type="button"
                class="lightbox-button lightbox-close"
                data-a11y-dialog-hide
                data-lightbox-close
              >
                <span class="visuallyhidden">
                  {i18n.p('Close', 'lightbox')}
                </span>
                <SvgIcon width="16" height="16">
                  <polygon points="16,2.1 13.9,0 8,5.9 2.1,0 0,2.1 5.9,8 0,13.9 2.1,16 8,10.1 13.9,16 16,13.9 10.1,8"></polygon>
                </SvgIcon>
              </button>
              <button
                type="button"
                class="lightbox-button lightbox-back-nav"
                data-node-name="back-nav"
                hidden
              >
                <SvgIcon width="16" height="16">
                  <path d="M8.4 0L1.57 6.827.4 8l1.173 1.173L8.4 16l1.173-1.173L2.8 8l6.773-6.827z"></path>
                </SvgIcon>
                <span class="text">{i18n.p('Back', 'lightbox')}</span>
              </button>
              <div class="lightbox-content" data-lightbox-content></div>
              <div class="lightbox-nav-container" data-lightbox-nav>
                <button
                  type="button"
                  class="lightbox-button lightbox-nav lightbox-nav--prev"
                  data-lightbox-prev
                >
                  <span class="visuallyhidden">
                    {i18n.p('Previous', 'lightbox')}
                  </span>
                  <SvgIcon width="18" height="30">
                    <polygon points="15.4,0 2.6,12.8 0.4,15 2.6,17.2 15.4,30 17.6,27.8 4.9,15 17.6,2.2"></polygon>
                  </SvgIcon>
                </button>
                <button
                  type="button"
                  class="lightbox-button lightbox-nav lightbox-nav--next"
                  data-lightbox-next
                >
                  <span class="visuallyhidden">
                    {i18n.p('Next', 'lightbox')}
                  </span>
                  <SvgIcon width="18" height="30">
                    <polygon points="15.4,12.8 2.6,0 0.4,2.3 13.1,15 0.4,27.8 2.6,30 15.4,17.3 17.6,15"></polygon>
                  </SvgIcon>
                </button>
              </div>
            </div>
            <div data-lightbox-prev-preload hidden></div>
            <div data-lightbox-next-preload hidden></div>
          </div>
        </div>
      );
      document.body.append(skeleton);
    }

    /**
     * Bind all required event listeners.
     */
    _bindEvents() {
      // React to open/close
      this.a11yModal.on('show', this._handleModalOpen);
      this.a11yModal.on('hide', this._handleModalClose);

      // The overlay is covered by the body and can't be clicked, so it must
      // be handled manually
      this.on(this.body, 'click', this._handleModalBodyClick);

      // Handle back nav button
      this.on(this.backNav, 'click', this._navigateBackwards);

      // Handle prev/next buttons
      this.on(this.prevButton, 'click', this._handlePrevClick);
      this.on(this.nextButton, 'click', this._handleNextClick);

      // Handle arrow keys for prev/next
      this.on(this.root, 'keyup', this._handleModalKeyup);

      // React to global open event
      this.onGlobal(LIGHTBOX_EVENT.open, this._handleGlobalOpen);

      // React to global close event
      this.onGlobal(LIGHTBOX_EVENT.close, this._handleGlobalClose);

      // React to global back nav event
      this.onGlobal(LIGHTBOX_EVENT.back, this._navigateBackwards);

      // React to form responses
      this.on(
        this.content,
        FORM_EVENT.response,
        'form',
        this._handleFormResponse,
        { capture: true },
      );
    }

    /**
     * Unbind all bound event listeners.
     */
    _unbindEvents() {
      this.a11yModal.off('show', this._handleModalOpen);
      this.a11yModal.off('hide', this._handleModalClose);
      this.off(this.body, 'click', this._handleModalBodyClick);
      this.off(this.backNav, 'click', this._navigateBackwards);
      this.off(this.prevButton, 'click', this._handlePrevClick);
      this.off(this.nextButton, 'click', this._handleNextClick);
      this.off(this.root, 'keyup', this._handleModalKeyup);
      this.offGlobal(LIGHTBOX_EVENT.open, this._handleGlobalOpen);
      this.offGlobal(LIGHTBOX_EVENT.close, this._handleGlobalClose);
      this.offGlobal(LIGHTBOX_EVENT.back, this._navigateBackwards);
      this.off(this.content, FORM_EVENT.response, this._handleFormResponse, {
        capture: true,
      });
    }

    /**
     * Handle successful form response.
     *
     * Navigates backwards if there is a nav stack with more than one url.
     * Closes lightbox if there is no nav stack.
     *
     * @param {Event} e - Form response event.
     * @param {object} data - Response data.
     * @param {Array.<object>} errors - A list of form errors.
     */
    _handleFormResponse = (e, data, errors) => {
      if (!errors.length) {
        if (this.navStack.length > 1) {
          this._navigateBackwards();
        } else {
          this.close();
        }
      }
    };

    /**
     * Handle global open event.
     *
     * Create a controller root/item trigger in memory, and open the modal
     * with that.
     *
     * @param {Event} e - Lightbox open event.
     * @param {string} content - Lightbox content, an HTML string or a URL.
     * @param {object} [options] - Options like `class` or `loadErrorText`.
     */
    _handleGlobalOpen = (e, content, options = {}) => {
      // The same event is used for both global and instance events, ignore
      // bubbling instance events.
      if (e.target === GLOBAL_EVENT_TARGET) {
        const isUrl = URL_REGEX.test(content);
        const item = document.createElement('button');
        setData(
          item,
          isUrl ? LIGHTBOX_URL_DATA_ATTRIBUTE : LIGHTBOX_CONTENT_DATA_ATTRIBUTE,
          content,
        );
        this.open(item, [item], 0, {
          ...options,
          eventTarget: GLOBAL_EVENT_TARGET,
        });
      }
    };

    /**
     * Handle global close event.
     *
     * @param {Event} e - Lightbox close event.
     * @param {Element} [content] - Lightbox content node, should not be set
     *   when the event is triggered globally with `triggerGlobalCustomEvent`.
     */
    _handleGlobalClose = (e, content) => {
      // The same event is used for both global and instance events, ignore
      // bubbling instance events.
      // For the global event the context can also differ. If the lightbox
      // has been opened programatically the global event target is used for
      // events triggered by DOM interactions since there is no other, 'real'
      // DOM node to trigger them on. When the lightbox is closed this way
      // the content will be set, which should not be the case when the global
      // close is used to programatically close it. Without checking for the
      // content, closing a programatically opened lightbox by clicking the
      // close button would therefore trigger a new close event, causing an
      // infinite event loop.
      if (e.target === GLOBAL_EVENT_TARGET && !content) {
        this.close();
      }
    };

    /**
     * Set classes when the modal has been opened.
     *
     * Since this runs after the modal is shown, some things is better run in
     * the open() method.
     */
    _handleModalOpen = () => {
      // Start listening for resize
      if (!this.isScrollable && this.type === TYPE_IMAGE) {
        this.on(window, 'resize', this._handleResize);
      }

      // Classes
      document.documentElement.classList.add(LIGHTBOX_OPEN_CLASS_NAME);
      if (this.isScrollable) {
        this.root.classList.add(LIGHTBOX_SCROLLABLE_CLASS_NAME);
        this.root.classList.remove(LIGHTBOX_CONTAINED_CLASS_NAME);
      } else {
        this.root.classList.add(LIGHTBOX_CONTAINED_CLASS_NAME);
        this.root.classList.remove(LIGHTBOX_SCROLLABLE_CLASS_NAME);
      }
    };

    /**
     * Remove the 'open' body class and reset state when the modal closes.
     */
    _handleModalClose = () => {
      if (!this.isScrollable && this.type === TYPE_IMAGE) {
        this.off(window, 'resize', this._handleResize);
      }

      document.documentElement.classList.remove(LIGHTBOX_OPEN_CLASS_NAME);
      if (this.customClassNames.length) {
        this.root.classList.remove(...this.customClassNames);
      }

      this.triggerCustomEvent(
        this.eventTarget,
        LIGHTBOX_EVENT.close,
        this.content,
        this.id,
      );

      this._setInitialState();

      // Reset content
      this.content.innerHTML = '';
    };

    /**
     * Prevent keyup from triggering the close button right after opening.
     *
     * @see _preventKeyboardClose()
     * @param {KeyboardEvent} e - Keydown event.
     */
    _handleCloseKeyup = (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
      }
      this._clearKeyboardClosePrevention();
    };

    /**
     * Remove the keyboard event listeners for the close button.
     *
     * @see _preventKeyboardClose()
     */
    _handleCloseKeydown = () => {
      this._clearKeyboardClosePrevention();
    };

    /**
     * Remove the keyboard event listeners for the close button.
     *
     * @see _preventKeyboardClose()
     */
    _clearKeyboardClosePrevention = () => {
      this.off(this.closeButton, 'keydown', this._handleCloseKeydown);
      this.off(this.closeButton, 'keyup', this._handleCloseKeyup);
    };

    /**
     * Prevent the initial keyup event from firing on the close button.
     *
     * This should be registered when the modal is opened with a keypress.
     *
     * When the modal is opened, the focus is set to the first focusable element
     * which can be the close button. If the modal is opened via keypress (e.g.
     * the space key on a trigger button) the release of the key will trigger
     * the click event (handled by a11y-dialog) on the newly focused close
     * button. Some very quick keypresses seem to not trigger this, but that
     * can likely vary between browsers and devices. To illustrate:
     *
     * Press space
     *   -> trigger.keydown
     *   -> modal open
     *   -> focus close button
     *   -> release key
     *   -> closeButton.keyup/click
     *   -> modal close
     *
     * Testing in Firefox, Chrome and Safari shows that the keyup event removes
     * itself properly on open, hopefully the same is true for other browsers.
     *
     * Pressing Enter seems to have its own behavior, triggering continuous
     * click events if held down. A fairly slow enter press doesn't trigger
     * the above problem though, so it's probably expected.
     */
    _preventKeyboardClose = () => {
      this.on(this.closeButton, 'keydown', this._handleCloseKeydown);
      this.on(this.closeButton, 'keyup', this._handleCloseKeyup);
    };

    /**
     * Close the modal if clicking in the modal body but outside the modal
     * content.
     *
     * @param {MouseEvent} e - Click event.
     */
    _handleModalBodyClick = (e) => {
      // The event `target` will be the element that first got the click, so a
      // click inside any content will not result in the body or figure being
      // the target.
      if (
        e.target === this.body ||
        e.target.classList.contains(LIGHTBOX_FIGURE_CLASS_NAME)
      ) {
        this.close();
      }
    };

    /**
     * Navigate when pressing left/right arrow keys.
     *
     * @param {KeyboardEvent} e - Keyup event.
     */
    _handleModalKeyup = (e) => {
      if (this.hasNav) {
        if (e.key === 'ArrowLeft') {
          this.showPrevItem();
        } else if (e.key === 'ArrowRight') {
          this.showNextItem();
        }
      }
    };

    /**
     * Navigate backwards in nav stack.
     */
    _navigateBackwards = () => {
      // Remove current view
      this.navStack.pop();
      // Get previous view
      const url = this.navStack[this.navStack.length - 1];
      this.showItem(0, url);
    };

    /**
     * Show previous item when clicking the previous button.
     */
    _handlePrevClick = () => {
      this.showPrevItem();
    };

    /**
     * Show next item when clicking the next button.
     */
    _handleNextClick = () => {
      this.showNextItem();
    };

    /**
     * Set the height limit on resize.
     */
    _handleResize = debounce(() => {
      this.limitHeight();
    }, 250);

    /**
     * Keep height within the viewport for default image view.
     */
    limitHeight = () => {
      const figure = selectSingle(
        this.content,
        `.${LIGHTBOX_FIGURE_CLASS_NAME}`,
      );
      if (figure) {
        const figureStyle = window.getComputedStyle(figure);
        const figureOffset = [
          'marginTop',
          'marginBottom',
          'paddingTop',
          'paddingBottom',
        ].reduce((total, prop) => total + parseInt(figureStyle[prop], 10), 0);
        const img = selectSingle(figure, 'img');
        const caption = selectSingle(figure, `.${LIGHTBOX_CAPTION_CLASS_NAME}`);
        const captionHeight = caption ? caption.offsetHeight : 0;
        // clientHeight on the HTML element is the viewport height when testing
        // and is supposed to be without scrollbars. Some comments indicate that
        // it can be the entire body height in some cases, so fall back to
        // innerHeight on the window which will be smaller then (though include
        // the height of a possible horizontal scrollbar). Also ensure the min
        // value isn't zero.
        const viewportHeight = Math.min.apply(
          null,
          [document.documentElement.clientHeight, window.innerHeight].filter(
            isTruthy,
          ),
        );
        const maxHeight = viewportHeight - captionHeight - figureOffset;
        img.style.maxHeight = `${maxHeight}px`;
      }
    };

    /**
     * Set aria-labelledby and remove aria-label.
     *
     * @param {string} [id] - HTML ID, defaults to lightbox title.
     */
    _setAriaLabelledby(id = LIGHTBOX_TITLE_ID) {
      this.root.removeAttribute('aria-label');
      this.root.setAttribute('aria-labelledby', id);
    }

    /**
     * Set aria-label and remove aria-labelledby.
     *
     * @param {string} label - Label text.
     */
    _setAriaLabel(label) {
      this.root.removeAttribute('aria-labelledby');
      this.root.setAttribute('aria-label', label);
    }

    /**
     * Set the ARIA live region text to be read by screen readers.
     *
     * @param {string} text
     */
    _setLiveRegionText(text) {
      this.liveRegion.textContent = text;
    }

    /**
     * Get the item at the specified index.
     *
     * @param {number} index - Item index.
     * @returns {Element}
     */
    _getItem(index) {
      if (!isNumber(index) || index < 0 || index > this.items.length - 1) {
        throw new RangeError(
          `Invalid index ${index}, expected 0–${this.items.length - 1}`,
        );
      }
      return this.items[index];
    }

    /**
     * Get the HTML content for the item at the specified index.
     *
     * @param {number} index - Item index.
     * @param {string} [url] - A url. Will be used instead of url attribute.
     * @returns {string}
     */
    async _getItemContent(index, url) {
      let content = '';

      const item = this._getItem(index);
      if (this.type === TYPE_HTML) {
        // eslint-disable-next-line no-param-reassign
        url = url || getData(item, LIGHTBOX_URL_DATA_ATTRIBUTE);
        content = url
          ? await httpGet(url)
          : getData(item, LIGHTBOX_CONTENT_DATA_ATTRIBUTE);
      } else {
        const captionText = getData(item, LIGHTBOX_CAPTION_DATA_ATTRIBUTE);
        // Ensure the caption has text to show, not just empty HTML elements.
        const caption =
          captionText && parseHTML(captionText).textContent
            ? `<figcaption class="${LIGHTBOX_CAPTION_CLASS_NAME}">${captionText}</figcaption>`
            : '';
        content = `<figure class="${LIGHTBOX_FIGURE_CLASS_NAME}"><img src="${item.href}" alt="">${caption}</figure>`;
      }
      return content;
    }

    /**
     * Show the content for the specified item.
     *
     * @param {number} index - Item index.
     * @param {string} [url] - A url. Will be used instead of url attribute.
     */
    async showItem(index, url) {
      this.currentIndex = index;

      // Set content
      try {
        this.isSettingHTML = true;
        // Wait for potential item changes, e.g. changing the content attribute
        // at the time of opening.
        await nextFrame();
        // Show a spinner if loading is slow.
        const itemContentPromise = this._getItemContent(this.currentIndex, url);
        const spinner = '<span class="lightbox-spinner"></span>';
        let itemContent = await Promise.race([
          itemContentPromise,
          sleep(400, spinner),
        ]);
        if (itemContent === spinner) {
          this._setAriaLabel('Loading');
          setInnerHTML(this.content, spinner);
          itemContent = await itemContentPromise;
        }
        setInnerHTML(this.content, itemContent);
        // Wait for HTML to change so that possible current lightbox nodes can
        // get destroyed by auto init.
        await nextFrame();
        this.isSettingHTML = false;
      } catch (err) {
        // Trigger error event and return early.
        const isCancelled = !this.triggerCancelableCustomEvent(
          this.eventTarget,
          LIGHTBOX_EVENT.ajaxError,
          err,
          this.id,
        );
        if (!isCancelled) {
          setInnerHTML(
            this.content,
            this.loadErrorText || i18n.t('Could not load'),
          );
          // Let potential other things run before re-throwing for Sentry.
          setTimeout(() => {
            throw err;
          }, 250);
        }
        return;
      }

      if (this.type === TYPE_HTML) {
        // Restore labelledby in case the loading label was used above.
        this._setAriaLabelledby();
        // Add id to heading for aria-labelledby.
        const firstHeading = selectSingle(this.content, 'h1, h2, h3');
        if (firstHeading) {
          firstHeading.id = LIGHTBOX_TITLE_ID;
        } else {
          // Avoid pointing to a nonexistent heading.
          this.root.removeAttribute('aria-labelledby');
          // eslint-disable-next-line no-console
          console.warn(
            'Lightbox with HTML content is missing a heading, at least one should be present for aria-labelledby.',
          );
        }
      } else if (this.type === TYPE_IMAGE) {
        // Let the content be inserted and rendered before limiting the height
        // since dimensions from the elements can't be read otherwise.
        afterNextPaint(this.limitHeight);
      }

      // Update the live region text
      this._setLiveRegionText(
        this.items.length > 1
          ? i18n
              .t('{{count}} of {{total}}.')
              .replace('{{count}}', index + 1)
              .replace('{{total}}', this.items.length)
          : '',
      );

      // Enable or disable back button
      if (this.navStack.length > 1) {
        this.backNav.removeAttribute('hidden');
      } else {
        this.backNav.setAttribute('hidden', '');
      }

      // Enable or disable prev/next buttons
      if (this.hasNav) {
        const hasPrev = index !== 0;
        const hasNext = index < this.items.length - 1;
        this.prevButton.disabled = !hasPrev;
        this.nextButton.disabled = !hasNext;
        if (hasPrev) {
          this.prevPreload.innerHTML = this._getItemContent(index - 1);
        }
        if (hasNext) {
          this.nextPreload.innerHTML = this._getItemContent(index + 1);
        }
      }

      initializer.init(this.root);

      // Trigger an show item event
      this.triggerCustomEvent(
        this.eventTarget,
        LIGHTBOX_EVENT.showItem,
        this.content,
        this.id,
      );
    }

    /**
     * Show the previous item if not at the start.
     */
    showPrevItem() {
      if (this.currentIndex > 0) {
        this.showItem(this.currentIndex - 1);
      }
    }

    /**
     * Show the next item if not at the end.
     */
    showNextItem() {
      if (this.currentIndex !== this.items.length - 1) {
        this.showItem(this.currentIndex + 1);
      }
    }

    /**
     * Open the modal with the specified set of items.
     *
     * @param {Element} controllerRoot - The lightbox controller root element
     * @param {Array.<object>} items - All available items.
     * @param {number} [currentIndex] - The current item index, defaults to 0.
     * @param {object} [options] - Options.
     */
    open(controllerRoot, items, currentIndex = 0, options = {}) {
      this.controllerRoot = controllerRoot;
      this.items = items;
      const item = this._getItem(currentIndex);
      this.type = TYPE_IMAGE;
      const url = getData(item, LIGHTBOX_URL_DATA_ATTRIBUTE);

      if (getData(item, LIGHTBOX_CONTENT_DATA_ATTRIBUTE) || url) {
        this.type = TYPE_HTML;
      }

      // Just using Object.assign will result in any null values from the
      // passed options overwriting defaults.
      const opts = conditionalAssign(not(isNil), DEFAULT_OPTIONS, options);
      this.hasNav = opts.hasNav && items.length > 1;
      this.id = opts.id;
      this.isScrollable = isBool(opts.isScrollable)
        ? opts.isScrollable
        : this.type === TYPE_HTML;
      this.customClassNames = opts.class.split(' ').filter(isTruthy);
      this.saveContentOnClose =
        opts.saveContentOnClose && this.type === TYPE_HTML;
      this.loadErrorText = opts.loadErrorText;
      this.eventTarget = opts.eventTarget || controllerRoot;

      // Do ARIA labelling before setting any content and showing the modal,
      // to ensure it's already there for screen readers.
      if (this.type === TYPE_HTML) {
        this._setAriaLabelledby();
      } else {
        this._setAriaLabel(
          this.items.length > 1
            ? i18n
                .t(
                  'Gallery with {{count}} images. Navigate with the left and right arrow keys.',
                )
                .replace('{{count}}', this.items.length)
            : i18n.t('Large image.'),
        );
      }

      // Set custom class names before opening to hopefully ensure no CSS
      // transitions trigger any flickering.
      if (this.customClassNames.length) {
        this.root.classList.add(...this.customClassNames);
      }

      this.showItem(currentIndex);
      if (url) {
        this.navStack.push(url);
      }
      if (!this.isOpen) {
        this.a11yModal.show();
        this.isOpen = true;
      }

      this._preventKeyboardClose();

      if (!this.hasNav) {
        this.nav.setAttribute('hidden', '');
      } else {
        this.nav.removeAttribute('hidden');
      }
    }

    /**
     * Close the modal if it's open.
     */
    close() {
      if (!this.isOpen) {
        return;
      }

      if (this.saveContentOnClose) {
        const item = this._getItem(this.currentIndex);
        setData(item, LIGHTBOX_CONTENT_DATA_ATTRIBUTE, this.content.innerHTML);
      }

      this.loadErrorText = null;
      this.navStack = [];
      this.backNav.setAttribute('hidden', '');

      this.a11yModal.hide();
    }
  }

  let singleton;
  return () => {
    if (!singleton) {
      singleton = new LightboxSkeleton();
    } else if (!document.getElementById(SKELETON_ID)) {
      // If the skeleton is manually removed from the DOM the instance will
      // still exist. In practice this should probably only happen in tests.
      singleton.destroy();
      singleton.init();
    }
    return singleton;
  };
})();

/**
 * Controller for each lightbox/modal group.
 */
export default class Lightbox extends ElementController {
  static id = LIGHTBOX_CONTROLLER_ID;

  static dependencies = ['ViewportHeight'];

  /**
   * DOM loaded, run initial modal setup and bind events.
   */
  init() {
    // Get lightbox instance
    this.modal = getLightboxSkeleton();

    // If this is the currently active lightbox
    this.setState({ isCurrent: false });

    this.setTriggersAndItems();
    this.buttonizeTriggers();
    this.bindEvents();
  }

  /**
   * Close the modal and unbind events when destroyed.
   */
  destroy() {
    // Modal content might include a Lightbox controller node.
    // Don't let auto init close modal when destroying that node.
    if (this.state.isCurrent && !this.modal.isSettingHTML) {
      this.modal.close();
    }

    this.off(this.root, 'keydown', this.handleButtonizedKeydown);
    this.off(this.root, 'click', this.handleRootClick);
    this.off(this.root, 'keydown', this.handleRootKeydown);
  }

  /**
   * Bind events by delegating to lightbox content.
   *
   * @see EventManager.on()
   * @param {string} eventName - Name of the event ('click', 'change'...).
   * @param {string|Function} delegateOrListener - Selector to delegate to, or
   *   event listener when not using delegation.
   * @param {Function} listener - Event listener when using delegation.
   * @param {object} [options] - Event listener options.
   * @example
   *
   * this.onLightbox('click', () => {
   *   // Called for click events that happen inside the open lightbox.
   * });
   */
  onLightbox(eventName, delegateOrListener, listener, options = {}) {
    this.on(this.root, LIGHTBOX_EVENT.showItem, (event, content) => {
      this.on(content, eventName, delegateOrListener, listener, options);
    });
    // Remove event when hiding
    this.modal.a11yModal.on('hide', () => {
      this.off(
        this.modal.content,
        eventName,
        listener || delegateOrListener,
        options,
      );
    });
  }

  /**
   * Get and cache relevant elements.
   */
  setTriggersAndItems() {
    // Prioritize defined items (including the root node), fall back to all
    // image links.
    const itemSelector = `[data-${LIGHTBOX_CONTENT_DATA_ATTRIBUTE}], [data-${LIGHTBOX_URL_DATA_ATTRIBUTE}]`;
    const { root } = this;
    let items = selectAll(root, itemSelector);
    if (!items.length) {
      if (
        root.matches(itemSelector) ||
        (root.nodeName === 'A' && IMAGE_HREF_REGEX.test(root.href))
      ) {
        items = [root];
      } else {
        items = selectAll(root, 'a').filter((a) =>
          IMAGE_HREF_REGEX.test(a.href),
        );
        this.triggers = items;
      }
    }

    // eslint-disable-next-line unicorn/consistent-destructuring
    if (!this.triggers) {
      // When not doing default images, handle the third docblock example with
      // nested triggers for one item.
      this.triggers = items.reduce((triggers, item) => {
        const childTriggers = selectAll(
          item,
          `[data-${LIGHTBOX_TRIGGER_DATA_ATTRIBUTE}]`,
        );
        return childTriggers.length
          ? triggers.concat(childTriggers)
          : triggers.concat(item);
      }, []);
    }

    // Get unique items based on either the content that is to be inserted or
    // the image URL linked to.
    this.items = items.filter(
      (item, i) =>
        items.findIndex((otherItem) => this.isItemEqual(item, otherItem)) === i,
    );
  }

  /**
   * Make non-button modal triggers behave like buttons.
   */
  buttonizeTriggers() {
    this.triggers
      .filter(
        (trigger) =>
          !['button', 'input', 'a'].includes(trigger.nodeName.toLowerCase()),
      )
      .forEach((trigger) => {
        trigger.setAttribute('role', 'button');
        // The tabIndex property defaults to -1 so it can't be used to check if
        // a tabindex is already set, must check the attribute.
        if (!trigger.hasAttribute('tabindex')) {
          trigger.tabIndex = 0;
        }
      });

    this.on(
      this.root,
      // Must be keydown rather than keyup to prevent a space press from
      // scrolling the page
      'keydown',
      '[role="button"]',
      this.handleButtonizedKeydown,
    );
  }

  /**
   * Bind event listeners.
   */
  bindEvents() {
    // Bind events for each group
    this.on(this.root, 'click', this.handleRootClick);
    this.on(this.root, 'keydown', this.handleRootKeydown);
  }

  /**
   * Check if two triggers represent the same lightbox content.
   *
   * @param {Element} triggerA - First trigger.
   * @param {Element} triggerB - Second trigger.
   * @returns {boolean}
   */
  isItemEqual(triggerA, triggerB) {
    const content = getData(triggerA, LIGHTBOX_CONTENT_DATA_ATTRIBUTE);
    if (content) {
      return getData(triggerB, LIGHTBOX_CONTENT_DATA_ATTRIBUTE) === content;
    }
    const url = getData(triggerA, LIGHTBOX_URL_DATA_ATTRIBUTE);
    if (url) {
      return getData(triggerB, LIGHTBOX_URL_DATA_ATTRIBUTE) === url;
    }
    return triggerB.href === triggerA.href;
  }

  /**
   * Check if a trigger is activated with a 'new tab action'.
   *
   * @param {Element} trigger - Trigger that was clicked/activated.
   * @returns {boolean}
   */
  isOpeningNewTab = (trigger) =>
    // Buttons can't be opened in a new tab
    trigger.nodeName.toLowerCase() === 'a' &&
    modifierKeyWatcher.isHoldingNewTabKey();

  /**
   * Get the trigger closest to the specified element.
   *
   * @param {Element} elem - Element to select from.
   * @returns {?Element}
   */
  getClosestTrigger(elem) {
    // The triggers can be explicit or implicit, see setTriggersAndItems
    const explicit = elem.closest(`[data-${LIGHTBOX_TRIGGER_DATA_ATTRIBUTE}]`);
    if (this.triggers.includes(explicit)) {
      return explicit;
    }
    const implicit = elem.closest(
      `a, [data-${LIGHTBOX_CONTENT_DATA_ATTRIBUTE}], [data-${LIGHTBOX_URL_DATA_ATTRIBUTE}]`,
    );
    if (this.triggers.includes(implicit)) {
      return implicit;
    }
    return null;
  }

  /**
   * Get the lightbox item for the specified trigger.
   *
   * @param {Element} trigger - Trigger element.
   * @returns {?Element}
   */
  getItemForTrigger(trigger) {
    // The items can be explicit or implicit, see setTriggersAndItems
    const explicit = trigger.closest(
      `[data-${LIGHTBOX_CONTENT_DATA_ATTRIBUTE}], [data-${LIGHTBOX_URL_DATA_ATTRIBUTE}]`,
    );
    if (this.items.includes(explicit)) {
      return explicit;
    }
    const implicit = trigger.closest('a');
    if (this.items.includes(implicit)) {
      return implicit;
    }
    return null;
  }

  /**
   * Open the modal if an event is from a trigger.
   *
   * @param {Event} event - Trigger element event (click, keydown...).
   */
  async maybeOpenModal(event) {
    // The target can be something like a span inside a trigger button, in
    // which case it should count.
    const trigger = this.getClosestTrigger(event.target);
    if (trigger && !this.isOpeningNewTab(trigger)) {
      event.preventDefault();
      const { items, modal, root } = this;
      this.setState({ isCurrent: true });
      // Since an item can have multiple triggers, the trigger itself may not
      // be an item.
      const item = this.getItemForTrigger(trigger);
      await modal.open(root, items, items.indexOf(item), {
        hasNav: getData(root, OPTION_HAS_NAV_DATA_ATTRIBUTE),
        id: root.getAttribute('id'),
        isScrollable: getData(root, OPTION_SCROLLABLE_DATA_ATTRIBUTE),
        class: getData(root, OPTION_CLASS_DATA_ATTRIBUTE),
        saveContentOnClose: getData(
          root,
          OPTION_SAVE_CONTENT_ON_CLOSE_DATA_ATTRIBUTE,
          Boolean,
        ),
        loadErrorText: getData(root, LOAD_ERROR_TEXT_DATA_ATTRIBUTE),
      });
      // Trigger an open event
      this.triggerCustomEvent(
        root,
        LIGHTBOX_EVENT.open,
        modal.content,
        modal.id,
      );
    }
  }

  /**
   * Open the modal when clicking a trigger.
   *
   * @param {MouseEvent} e - Click event.
   */
  handleRootClick = (e) => {
    this.maybeOpenModal(e);
  };

  /**
   * Open the modal if activating a trigger with the keyboard.
   *
   * @param {KeyboardEvent} e - Keydown event.
   */
  handleRootKeydown = (e) => {
    // Buttons can be activated with enter or space.
    if (e.key === 'Enter' || e.key === ' ') {
      this.maybeOpenModal(e);
    }
  };

  /**
   * Open the modal if activating a buttonized trigger with the keyboard.
   *
   * @param {KeyboardEvent} e - Keydown event.
   */
  handleButtonizedKeydown = (e) => {
    // Buttons can be activated with enter or space.
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.maybeOpenModal(e);
    }
  };
}
